'use client';
import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import ReviewPostCard from './ReviewPostCard';
import AuthGateModal from '@/components/AuthGateModal';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface Review {
  id: string;
  cafeId: string;
  cafeName: string;
  userId: string;
  userName: string;
  userAvatar: string;
  userHandle: string;
  isVerified: boolean;
  rating: number;
  text: string;
  photos: string[];
  likes: number;
  comments: number;
  timeAgo: string;
  isLiked: boolean;
  isSaved: boolean;
  visitType: string;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m lalu`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}j lalu`;
  const days = Math.floor(hrs / 24);
  return `${days}h lalu`;
}

export default function DiscoveryFeed() {
  const supabase = createClient();
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authAction, setAuthAction] = useState('fitur ini');

  const fetchReviews = async () => {
    const { data, error } = await supabase
      .from('reviews')
      .select(`
        id,
        coffee_shop_id,
        user_id,
        rating,
        content,
        visit_type,
        photo_urls,
        like_count,
        comment_count,
        created_at,
        coffee_shops(name),
        user_profiles(full_name, username, avatar_url, is_verified_reviewer)
      `)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      console.log('Feed fetch error:', error.message);
      setLoading(false);
      return;
    }

    const mapped: Review[] = (data || []).map((r: any) => ({
      id: r.id,
      cafeId: r.coffee_shop_id,
      cafeName: r.coffee_shops?.name || 'Cafe',
      userId: r.user_id,
      userName: r.user_profiles?.full_name || r.user_profiles?.username || 'Nongki User',
      userAvatar: r.user_profiles?.avatar_url || '',
      userHandle: `@${r.user_profiles?.username || 'user'}`,
      isVerified: r.user_profiles?.is_verified_reviewer || false,
      rating: r.rating,
      text: r.content,
      photos: r.photo_urls || [],
      likes: r.like_count || 0,
      comments: r.comment_count || 0,
      timeAgo: timeAgo(r.created_at),
      isLiked: false,
      isSaved: false,
      visitType: r.visit_type || 'Santai',
    }));

    setReviews(mapped);
    setLoading(false);
  };

  useEffect(() => {
    fetchReviews();

    // Real-time subscription for new reviews
    const channel = supabase
      .channel('reviews_feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'reviews' }, () => {
        fetchReviews();
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'reviews' }, (payload) => {
        setReviews((prev) =>
          prev.map((r) =>
            r.id === payload.new.id
              ? { ...r, likes: payload.new.like_count || r.likes, comments: payload.new.comment_count || r.comments }
              : r
          )
        );
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const requireAuth = (action: string) => {
    setAuthAction(action);
    setAuthModalOpen(true);
  };

  const handleLike = async (reviewId: string) => {
    if (!user) return;
    const review = reviews.find((r) => r.id === reviewId);
    if (!review) return;

    if (review.isLiked) {
      await supabase.from('review_likes').delete().eq('review_id', reviewId).eq('user_id', user.id);
      await supabase.from('reviews').update({ like_count: Math.max(0, review.likes - 1) }).eq('id', reviewId);
    } else {
      await supabase.from('review_likes').insert({ review_id: reviewId, user_id: user.id });
      await supabase.from('reviews').update({ like_count: review.likes + 1 }).eq('id', reviewId);
    }

    setReviews((prev) =>
      prev.map((r) =>
        r.id === reviewId
          ? { ...r, isLiked: !r.isLiked, likes: r.isLiked ? r.likes - 1 : r.likes + 1 }
          : r
      )
    );
  };

  const handleSave = async (reviewId: string) => {
    if (!user) return;
    const review = reviews.find((r) => r.id === reviewId);
    if (!review) return;

    if (!review.isSaved) {
      await supabase.from('saved_places').insert({
        user_id: user.id,
        coffee_shop_id: review.cafeId,
        collection_name: 'Favorit',
      });
    }

    setReviews((prev) =>
      prev.map((r) => (r.id === reviewId ? { ...r, isSaved: !r.isSaved } : r))
    );
  };

  if (loading) {
    return (
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="section-title">✨ Feed Komunitas</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Cerita terbaru dari para Nongki-ers</p>
          </div>
        </div>
        <div className="flex justify-center py-12">
          <Loader2 size={24} className="animate-spin text-accent" />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="section-title">✨ Feed Komunitas</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Cerita terbaru dari para Nongki-ers</p>
        </div>
        <span className="text-xs text-green-DEFAULT font-600 flex items-center gap-1">
          <span className="w-2 h-2 bg-green-DEFAULT rounded-full animate-pulse" />
          Live
        </span>
      </div>

      <div className="flex flex-col gap-4">
        {reviews.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">☕</div>
            <p className="text-sm font-600 text-primary mb-1">Belum ada review</p>
            <p className="text-xs text-muted-foreground">Jadilah yang pertama review coffeeshop Malang!</p>
          </div>
        ) : (
          reviews.map((review) => (
            <ReviewPostCard
              key={`feed-${review.id}`}
              review={review}
              onLike={() => user ? handleLike(review.id) : requireAuth('like review')}
              onComment={() => requireAuth('comment')}
              onSave={() => user ? handleSave(review.id) : requireAuth('simpan tempat')}
              onLikeConfirmed={() => handleLike(review.id)}
              onSaveConfirmed={() => handleSave(review.id)}
            />
          ))
        )}
      </div>

      <AuthGateModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        action={authAction}
      />
    </div>
  );
}